import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import Draggable from "react-draggable";
import { CloseOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Modal, Button, Typography, Form, Row, Spin } from "antd";
import { useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  BranchEmployeesSelect,
  SettingsDataSelect,
} from "../../../forms/AutoCompleteFormItem";
import { getEmployee } from "../../../../api/server";
import { InputItem } from "../../../forms/FormItems";
import CheckBoxItem from "../../../forms/CheckBoxItem";
import AnimationChildWrapper from "../../../animations/AnimationChildWrapper";
import ChildrenAnimationWrapper from "../../../animations/ChildrenAnimationWrapper";

const { Title, Text } = Typography;
const TransferModal = (props) => {
  const {
    onHide,
    visible,
    onTransfer,
    employeeId,
    selectedEvents,
    selectedGhosts,
    branchId,
  } = props;
  const intl = useIntl();
  let location = useLocation();
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState(true);
  const [values, setValues] = useState({});
  const [replacer, setReplacer] = useState(null);
  const [loadingEmployee, setLoadingEmployee] = useState(null);

  const loading = useSelector(
    (state) => state.pendingActions["scheduling/transferEmployeeScheduling"]
  );
  const onCancel = () => {
    form.resetFields();
    onHide();
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      const isAdmin = location.pathname.includes("admin");
      const params = {
        id: values.replacer_id,
        branchId: branchId,
        admin: isAdmin,
      };
      setLoadingEmployee(true);
      let response = await getEmployee(params);
      setLoadingEmployee(false);
      setReplacer(response.data);
    };

    if (values.replacer_id) {
      fetchEmployee().catch(console.error);
    } else {
      setReplacer(null);
    }
  }, [values.replacer_id, branchId]);

  useEffect(() => {
    if (replacer) {
      form.setFieldValue("tariff_id", replacer.tariff_id);
      setValues({ ...values, tariff_id: replacer.tariff_id });
    }
  }, [replacer]);

  useEffect(() => {
    form.setFieldValue("use_employee_tariff", true);
  }, []);

  useEffect(() => {
    if (!visible) {
      setValues({});
      setReplacer(null);
    }
  }, [visible]);

  const onMouseOver = () => {
    setDisabled(false);
  };

  const onMouseOut = () => {
    setDisabled(true);
  };

  const caregiverFilter = {
    $and: [
      { $or: [{ status: 1 }] },
      {
        id: {
          $neq: employeeId,
        },
      },
    ],
  };

  const layout = {
    labelCol: { span: 9 },
    wrapperCol: { span: 15 },
  };

  const onValuesChange = (changedValues, allValues) => {
    setValues(allValues);
  };

  const onFinish = (values) => {
    const params = {
      branch_id: branchId,
      from_employee_id: employeeId,
      to_employee_id: values.replacer_id,
      transportation_cost: values.transportation_cost,
      tariff_id: values.tariff_id,
      use_employee_tariff: values.use_employee_tariff,
      events: selectedEvents,
      replacements: selectedGhosts,
    };
    onTransfer(params, form);
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const activeTariffFilter = {
    $and: [
      {
        $or: [{ active: true }],
      },
    ],
  };

  return (
    <Modal
      closable={false}
      footer={null}
      maskClosable={false}
      title={null}
      width={"600px"}
      styles={{
        body: {
          padding: 0,
        },
      }}
      open={visible}
      destroyOnClose={true}
      modalRender={(modal) => (
        <Draggable disabled={disabled}>{modal}</Draggable>
      )}
    >
      <div>
        <div
          style={{
            width: "100%",
            cursor: "move",
            height: "50px",
            position: "absolute",
            left: "0",
            top: "0",
          }}
          className="header"
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
        >
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            className="closeBtn"
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px 40px 40px 40px",
          }}
        >
          <Title
            style={{
              fontSize: "30px",
              fontWeight: 500,
              lineHeight: 1.27,
              color: "rgba(0, 0, 0, 0.85)",
              margin: "13px 0 40px 0 ",
              textAlign: "center",
            }}
          >
            {<FormattedMessage id="app.absences.transfer_sheduling_title" />}
          </Title>
          <div
            style={{ display: "flex", flexDirection: "column", width: "300px" }}
          >
            <Form
              {...layout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              form={form}
              onValuesChange={onValuesChange}
            >
              <ChildrenAnimationWrapper>
                <Row>
                  <div style={{ flex: 1 }}>
                    <AnimationChildWrapper itemKey={1}>
                      <BranchEmployeesSelect
                        name="replacer_id"
                        category="employee"
                        getEmployee={branchId !== null}
                        required={true}
                        extraParams={{
                          filter: caregiverFilter,
                          scopePrefix: `branches/${branchId}/`,
                        }}
                        placeholder={intl.formatMessage({
                          id: "app.absences.transfer_sheduling_placeholder",
                        })}
                        withId
                      />
                    </AnimationChildWrapper>
                    <AnimationChildWrapper itemKey={2}>
                      <SettingsDataSelect
                        name="tariff_id"
                        category="employee"
                        settingsdataName={"tariffs"}
                        required={true}
                        disabled={!values.replacer_id}
                        extraParams={{
                          filter: activeTariffFilter,
                        }}
                        type="list"
                      />
                    </AnimationChildWrapper>
                    <AnimationChildWrapper itemKey={3}>
                      <InputItem
                        name="transportation_cost"
                        category="employee"
                        number={true}
                        twoDigitsNumbers={true}
                        onlyPositiveNumbers={true}
                        style={{ margin: 0 }}
                      />
                      {replacer ? (
                        loadingEmployee ? (
                          <Spin />
                        ) : (
                          <Text>
                            {
                              <FormattedMessage
                                id={`app.forms.employee.${replacer.transportation_cost_strategy}`}
                              />
                            }
                          </Text>
                        )
                      ) : null}
                    </AnimationChildWrapper>

                    <AnimationChildWrapper itemKey={3}>
                      <CheckBoxItem
                        name="use_employee_tariff"
                        category="employee"
                        style={{ marginBottom: 0 }}
                      />
                    </AnimationChildWrapper>

                    <AnimationChildWrapper itemKey={4}>
                      <div
                        style={{
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                        }}
                      >
                        <Button
                          htmlType="submit"
                          style={{ marginTop: "20px" }}
                          type="primary"
                          disabled={!values.replacer_id || !values.tariff_id}
                          loading={loading}
                        >
                          {
                            <FormattedMessage id="app.actions.transfer_sheduling" />
                          }
                        </Button>
                      </div>
                    </AnimationChildWrapper>
                  </div>
                </Row>
              </ChildrenAnimationWrapper>
            </Form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TransferModal;
